import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { TRANSLATIONS } from '../i18n/translations';

const LanguageContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k, dir: 'ltr', suggestedLang: null });

// Country → language via IANA timezone. Runs offline & instantly — no
// geo-IP call needed. Only consulted when the browser language itself
// isn't one of our supported languages.
const TZ_LANGUAGE_MAP = [
  { lang: 'tl', zones: ['Asia/Manila'] },
  { lang: 'pt', zones: ['America/Sao_Paulo', 'America/Bahia', 'America/Fortaleza', 'America/Recife', 'America/Belem', 'America/Manaus', 'America/Campo_Grande', 'America/Cuiaba', 'America/Maceio', 'America/Porto_Velho', 'America/Boa_Vista', 'America/Rio_Branco', 'America/Santarem', 'America/Araguaina', 'America/Noronha', 'Europe/Lisbon', 'Atlantic/Azores', 'Atlantic/Madeira'] },
  { lang: 'es', zones: ['Europe/Madrid', 'Atlantic/Canary', 'America/Mexico_City', 'America/Monterrey', 'America/Tijuana', 'America/Cancun', 'America/Merida', 'America/Chihuahua', 'America/Hermosillo', 'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Argentina/', 'America/Caracas', 'America/Guayaquil', 'America/La_Paz', 'America/Asuncion', 'America/Montevideo', 'America/Guatemala', 'America/Tegucigalpa', 'America/Managua', 'America/El_Salvador', 'America/Costa_Rica', 'America/Panama', 'America/Santo_Domingo', 'America/Havana'] },
  { lang: 'fr', zones: ['Europe/Paris', 'America/Montreal', 'Europe/Monaco', 'Europe/Luxembourg'] },
  { lang: 'de', zones: ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich'] },
  { lang: 'ar', zones: ['Asia/Riyadh', 'Asia/Dubai', 'Asia/Kuwait', 'Asia/Qatar', 'Asia/Bahrain', 'Asia/Muscat', 'Asia/Baghdad', 'Asia/Amman', 'Asia/Beirut', 'Asia/Damascus', 'Africa/Cairo', 'Africa/Casablanca', 'Africa/Algiers', 'Africa/Tunis', 'Africa/Tripoli', 'Asia/Aden', 'Asia/Hebron', 'Asia/Gaza'] },
];

const langFromTimezone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    for (const { lang, zones } of TZ_LANGUAGE_MAP) {
      if (zones.some(z => tz === z || (z.endsWith('/') && tz.startsWith(z)))) return lang;
    }
  } catch { /* noop */ }
  return null;
};

const detectLang = () => {
  try {
    // 1. Explicit choice always wins
    const saved = localStorage.getItem('hibff_lang');
    if (saved && TRANSLATIONS[saved]) return saved;
    // 2. Browser language (usually mirrors the user's real language)
    const navLangs = navigator.languages || [navigator.language || 'en'];
    for (const l of navLangs) {
      const code = (l || '').slice(0, 2).toLowerCase();
      if (TRANSLATIONS[code]) return code;
      if (code === 'fi' && (l || '').toLowerCase().startsWith('fil')) return 'tl';
    }
    // 3. Country inferred from device timezone
    const tzLang = langFromTimezone();
    if (tzLang) return tzLang;
    return 'en';
  } catch {
    return 'en';
  }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLang);
  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem('hibff_lang', l); } catch {}
  }, []);
  const t = useCallback(
    (key) => TRANSLATIONS[lang]?.[key] ?? TRANSLATIONS.en[key] ?? key,
    [lang]
  );
  const suggestedLang = useMemo(() => langFromTimezone(), []);
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  return (
    <LanguageContext.Provider value={{ lang, setLang, t, dir, suggestedLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
